import {
  createFileRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import {
  type ClairePrefillEntityType,
  isClairePrefillEntityType,
  useAssistantUsage,
} from '@/features/assistant';
import {
  clearAssistantHandoff,
  readAssistantHandoff,
} from '@/features/assistant/lib/pending-handoff';
import {
  ChatContainer,
  ChatSkeleton,
  ConversationList,
  FreeTierGate,
} from './assistant/-components';

const assistantSearchSchema = z
  .object({
    prefill: z.string().optional(),
    entityType: z.string().optional(),
    entityId: z.string().optional(),
    id: z.string().optional(),
    // TanStack's default search parser JSON-parses primitive values, so the
    // natural `?new=1` URL arrives as the number 1 on a hard navigation.
    new: z.coerce.string().optional(),
    handoff: z.string().optional(),
  })
  .transform((s) => ({
    prefill: s.prefill,
    entityId: s.entityId,
    entityType:
      s.entityType && isClairePrefillEntityType(s.entityType)
        ? s.entityType
        : undefined,
    id: s.id,
    new: s.new,
    handoff: s.handoff,
  }));

export const Route = createFileRoute('/_authed/assistant')({
  validateSearch: assistantSearchSchema,
  component: AssistantPage,
});

function AssistantPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // `?id=xxx` → specific conversation; `?new=1` → new chat; else → list
  const activeConversationId = search.id ?? undefined;
  const isNewChat = search.new === '1';
  const { usage, isLoading: usageLoading } = useAssistantUsage();

  // Hand-off from the home-screen prompt (draft text + picked files), keyed by
  // the `?handoff=` token so the read is idempotent — this page's render phase
  // can run more than once per navigation (chrome swap, lazy chunk, discarded
  // render), and a store that cleared itself on first read used to drop the
  // draft on the floor. Released only after dispatch, so upload failures
  // retain the draft and retry controls.
  // When a hand-off is present the assistant auto-sends on arrival, so the
  // user lands straight in a live conversation (no welcome screen).
  const handoffToken = search.handoff;
  const handoff = readAssistantHandoff(handoffToken);
  const initialFiles = handoff?.dispatchedAt ? undefined : handoff?.files;
  const autoSend = Boolean(
    handoff && !handoff.autoSendClaimed && !handoff.dispatchedAt
  );

  const [initialDraft] = useState<string | undefined>(() =>
    handoff?.dispatchedAt
      ? undefined
      : (handoff?.draft ?? search.prefill ?? undefined)
  );

  const [initialContext] = useState<
    { entityType: ClairePrefillEntityType; entityId: string } | undefined
  >(() => {
    if (handoff?.dispatchedAt) return undefined;
    const rawType = search.entityType;
    const rawId = search.entityId;
    if (!rawType || !rawId) return undefined;
    const trimmedId = rawId.trim();
    if (!trimmedId) return undefined;
    return { entityType: rawType, entityId: trimmedId };
  });

  const handleSelectConversation = useCallback(
    (id: string) => {
      void navigate({ to: '/assistant', search: { id } });
    },
    [navigate]
  );

  // "New Chat" → show the chat surface with no active conversation
  const handleNewChat = useCallback(() => {
    void navigate({ to: '/assistant', search: { new: '1' } });
  }, [navigate]);

  const handleDeleteActive = useCallback(() => {
    void navigate({ to: '/assistant', search: {} });
  }, [navigate]);

  const handleBack = useCallback(() => {
    void navigate({ to: '/assistant', search: {} });
  }, [navigate]);

  // Which chat the mounted `ChatContainer` belongs to. `ChatContainer` pins its
  // mode at mount (so a brand-new chat keeps its in-flight `useChat` when the
  // URL flips from `?new=1` to `?id=…`), which means it must be REMOUNTED when
  // the user actually picks a different chat — from the sidebar chat history
  // (ENG-1161) that happens while the chat is already on screen:
  //   - opening another conversation, or "+" for a fresh one → new generation
  //     → new `key` → remount;
  //   - the URL adopting the id of the chat this mount just created (first
  //     turn dispatched) → same generation → no remount.
  // Adjusted during render (React's "storing information from previous
  // renders" pattern) so the remount happens in the same commit as the URL
  // change, with no stale frame.
  const chatTarget = activeConversationId
    ? `id:${activeConversationId}`
    : isNewChat
      ? 'new'
      : 'none';
  const [chatSession, setChatSession] = useState<{
    target: string;
    adoptedId: string | undefined;
    generation: number;
  }>({ target: chatTarget, adoptedId: undefined, generation: 0 });
  if (chatSession.target !== chatTarget) {
    const adopted =
      activeConversationId !== undefined &&
      activeConversationId === chatSession.adoptedId;
    setChatSession({
      target: chatTarget,
      adoptedId: undefined,
      generation: adopted ? chatSession.generation : chatSession.generation + 1,
    });
  }
  // A send may dispatch after its ChatContainer unmounts. Its adoption
  // callback must not replace the conversation the user opened meanwhile.
  const currentGenerationRef = useRef(chatSession.generation);
  currentGenerationRef.current = chatSession.generation;
  // Prefill / entity context / hand-off belong to the chat the page was OPENED
  // with; a later "+" must not resurrect the arrival draft.
  const isArrivalChat = chatSession.generation === 0;

  const handleFirstTurnDispatched = useCallback(
    (id: string) => {
      // A later manual new-chat visit has the same URL but owns a different
      // view. Only a still-owned handoff may finish across a page remount.
      if (
        !mountedRef.current &&
        (!handoffToken ||
          readAssistantHandoff(handoffToken)?.conversationId !== id)
      )
        return;
      if (currentGenerationRef.current !== chatSession.generation) return;
      // A new chat can remount during async conversation creation. The
      // original sender may still finish, but may only adopt its own URL;
      // leaving the page or opening another chat must not be undone by it.
      const liveSearch = router.state.location.search;
      if (
        router.state.location.pathname !== '/assistant' ||
        liveSearch.id !== activeConversationId ||
        (String(liveSearch.new) === '1') !== isNewChat ||
        liveSearch.handoff !== handoffToken
      )
        return;
      setChatSession((session) => ({ ...session, adoptedId: id }));
      // Dispatch follows uploads; conversation creation alone cannot consume
      // the draft. Reloads can now recover the owned server conversation.
      clearAssistantHandoff(handoffToken);
      // Update the URL silently without re-mounting the component tree.
      // TanStack's `navigate` with `replace` updates search params in place;
      // `ChatContainer` ignores `conversationId` changes once mounted in
      // "new chat" mode, so the in-flight useChat instance is preserved.
      void navigate({ to: '/assistant', search: { id }, replace: true });
    },
    [
      navigate,
      router,
      activeConversationId,
      isNewChat,
      handoffToken,
      chatSession.generation,
    ]
  );

  // A reload can land between recording dispatch and updating the URL.
  useEffect(() => {
    if (handoff?.dispatchedAt && handoff.conversationId) {
      handleFirstTurnDispatched(handoff.conversationId);
    }
  }, [
    handoff?.dispatchedAt,
    handoff?.conversationId,
    handleFirstTurnDispatched,
  ]);

  const content = (() => {
    if (usageLoading) {
      return <ChatSkeleton />;
    }

    if (usage && !usage.hasAccess) {
      return <FreeTierGate />;
    }

    // Full-page chat when viewing a specific conversation or starting a new one
    if (
      activeConversationId ||
      isNewChat ||
      (isArrivalChat && (initialDraft || initialContext)) ||
      handoff
    ) {
      return (
        <ChatContainer
          key={chatSession.generation}
          conversationId={activeConversationId ?? handoff?.conversationId}
          onFirstTurnDispatched={handleFirstTurnDispatched}
          onBack={handleBack}
          initialDraft={isArrivalChat ? initialDraft : undefined}
          initialContext={isArrivalChat ? initialContext : undefined}
          initialFiles={initialFiles}
          initialUploads={handoff?.dispatchedAt ? undefined : handoff?.uploads}
          autoSend={autoSend}
          handoffToken={handoffToken}
        />
      );
    }

    // Full-page conversation list
    return (
      <ConversationList
        activeId={activeConversationId}
        onSelect={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteActive={handleDeleteActive}
      />
    );
  })();

  return (
    <>
      <title>Assistant | Borrah</title>
      {content}
    </>
  );
}
